import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Coins, Infinity as Inf, Receipt, Crown, ArrowUpRight, Settings } from "lucide-react";
import { DevicePairing } from "@/components/DevicePairing";
import { SoundLibrary } from "@/components/SoundLibrary";

const StatCard = ({ label, value, accent = "text-slate-50", testid }) => (
  <div data-testid={testid} className="card-surface rounded-xl p-5">
    <p className="label-mono text-slate-500">{label}</p>
    <p className={`font-display font-bold text-2xl mt-1.5 ${accent}`}>{value}</p>
  </div>
);

export default function Profile() {
  const { user, isPrivileged } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/me/profile").then((r) => setProfile(r.data));
    api.get("/me/ledger").then((r) => setLedger(r.data));
    api.get("/billing/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const portal = async () => {
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error("No billing account yet, subscribe first."); }
  };

  const bal = profile?.balance;
  const plan = profile?.plan;

  return (
    <div className="min-h-screen">
      <TopNav />
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-4 mb-9">
          {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl ring-1 ring-white/15" />
            : <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-cyan text-2xl font-bold">{(user?.name || "?")[0]}</div>}
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-3xl text-slate-50">{user?.name}</h1>
            <p className="text-slate-400">{user?.email}</p>
          </div>
          {isPrivileged && <span className="ml-auto label-mono px-3 py-1.5 rounded-full text-orange bg-orange-500/10 flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" /> {user.role === "super_admin" ? "Super Admin" : "Admin"}</span>}
        </div>

        {/* subscription */}
        <div className="tracing-border rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="label-mono text-slate-500 mb-1">Subscription</p>
            <p className="font-display font-bold text-2xl text-slate-50">{isPrivileged ? "Pro Studio (complimentary)" : (plan ? plan.name : "No active plan")}</p>
            <p className="text-slate-400 text-sm mt-1">Status: <span className={isPrivileged || user?.subscription_status === "active" ? "text-cyan" : "text-amber"}>{isPrivileged ? "privileged" : (user?.subscription_status || "none")}</span>
              {profile?.user?.current_period_end && <span className="text-slate-500"> · renews {profile.user.current_period_end.slice(0,10)}</span>}</p>
          </div>
          <div className="flex gap-2">
            {!isPrivileged && (user?.subscription_status === "active"
              ? <Button data-testid="manage-sub-btn" onClick={portal} className="rounded-full bg-white/10 hover:bg-white/15 font-semibold"><Settings className="w-4 h-4 mr-1.5" /> Manage</Button>
              : <Button data-testid="upgrade-btn" onClick={() => nav("/pricing")} className="rounded-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold">Choose plan <ArrowUpRight className="w-4 h-4 ml-1" /></Button>)}
          </div>
        </div>

        {/* credit stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-available" label="Available" accent="text-cyan" value={isPrivileged ? <span className="flex items-center gap-1"><Inf className="w-5 h-5" />Unlimited</span> : (bal?.available?.toLocaleString?.() ?? 0)} />
          <StatCard testid="stat-reserved" label="Reserved" value={isPrivileged ? ", " : (bal?.reserved ?? 0)} />
          <StatCard testid="stat-used" label="Used (30d)" accent="text-violet" value={isPrivileged ? ", " : (bal?.used_this_cycle ?? 0)} />
          <StatCard testid="stat-projects" label="Projects" accent="text-amber" value={profile?.projects ?? 0} />
        </div>

        {/* devices */}
        <div className="mb-8"><DevicePairing /></div>

        {/* sound library */}
        <div className="mb-8"><SoundLibrary /></div>

        {/* ledger */}
        <div className="card-surface rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4"><Coins className="w-5 h-5 text-amber" /><h2 className="font-display font-semibold text-lg text-slate-50">Credit ledger</h2></div>          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-mono text-slate-500 border-b border-white/10">
                <th className="pb-2">Type</th><th className="pb-2">Description</th><th className="pb-2 text-right">Amount</th><th className="pb-2 text-right">Balance</th><th className="pb-2 text-right">Date</th>
              </tr></thead>
              <tbody data-testid="ledger-table">
                {ledger.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-600">No ledger entries yet.</td></tr>}
                {ledger.map((l) => (
                  <tr key={l.id} className="border-b border-white/5">
                    <td className="py-2.5"><span className="label-mono text-slate-400">{l.entry_type}</span></td>
                    <td className="py-2.5 text-slate-300">{l.description}</td>
                    <td className={`py-2.5 text-right font-mono-a ${l.amount_signed >= 0 ? "text-cyan" : "text-slate-400"}`}>{l.amount_signed >= 0 ? "+" : ""}{l.amount_signed}</td>
                    <td className="py-2.5 text-right font-mono-a text-slate-500">{l.balance_after}</td>
                    <td className="py-2.5 text-right text-slate-600 text-xs">{(l.created_at || "").slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* invoices */}
        <div className="card-surface rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4"><Receipt className="w-5 h-5 text-cyan" /><h2 className="font-display font-semibold text-lg text-slate-50">Payment history</h2></div>
          {invoices.length === 0 ? <p className="text-slate-600 text-sm">No payments yet.</p> : (
            <div className="space-y-2" data-testid="invoices-list">
              {invoices.map((i) => (
                <div key={i.session_id} className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                  <div><p className="text-slate-200 text-sm capitalize">{i.kind?.replace("_"," ")} · {i.lookup_key}</p><p className="text-slate-600 text-xs">{(i.created_at||"").slice(0,10)}</p></div>
                  <span className="font-display font-bold text-slate-50">${i.amount} <span className="text-slate-500 text-xs uppercase">{i.currency}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
